'use client';

import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';

export default function Page() {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));

  // M8: cross-region POC test mode. When a region is selected, all participants
  // join the SAME fixed room name ('poc-mesh-test') but each browser connects to
  // its chosen SFU. If the relay (M3-M7) works, browsers on different SFUs see
  // each other's video. If not, they only see same-region peers.
  const [pocRegion, setPocRegion] = useState<'india-poc' | 'us-poc'>('india-poc');

  const startMeeting = () => {
    if (e2ee) {
      router.push(`/rooms/${generateRoomId()}#${encodePassphrase(sharedPassphrase)}`);
    } else {
      router.push(`/rooms/${generateRoomId()}`);
    }
  };

  const joinPocMesh = () => {
    // Fixed room name so two browsers — each picking a different region —
    // join the SAME logical room and exercise the relay between them.
    router.push(`/rooms/poc-mesh-test?region=${pocRegion}`);
  };

  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className="header">
          <img
            src="/images/ultrakin-connect-logo.svg"
            alt="Ultrakin Connect"
            width="360"
            height="45"
          />
          <h2>
            Professional video conferencing platform built for seamless collaboration and real-time
            communication.
          </h2>
        </div>
        <div className={styles.tabContent}>
          <p style={{ margin: 0 }}>Start an instant video meeting with Ultrakin Connect.</p>
          <button style={{ marginTop: '1rem' }} className="lk-button" onClick={startMeeting}>
            Start Meeting
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
              <input
                id="use-e2ee"
                type="checkbox"
                checked={e2ee}
                onChange={(ev) => setE2ee(ev.target.checked)}
              ></input>
              <label htmlFor="use-e2ee">Enable end-to-end encryption</label>
            </div>
            {e2ee && (
              <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
                <label htmlFor="passphrase">Passphrase</label>
                <input
                  id="passphrase"
                  type="password"
                  value={sharedPassphrase}
                  onChange={(ev) => setSharedPassphrase(ev.target.value)}
                />
              </div>
            )}
          </div>

          {/* M8 — Cross-region SFU mesh POC. Visually separate from the main
              flow so it's clearly an internal test path, not regular product. */}
          <div
            style={{
              marginTop: '2rem',
              padding: '1rem 1.25rem',
              border: '1px dashed #8a8a82',
              borderRadius: '8px',
              background: 'rgba(249, 115, 22, 0.06)',
            }}
          >
            <strong style={{ fontSize: '0.95rem' }}>POC: Cross-region mesh test</strong>
            <p style={{ margin: '0.4rem 0 0.8rem', fontSize: '0.85rem', opacity: 0.85 }}>
              Open two browsers, pick different regions in each, click Join. Both land in the same
              room — one routed through India SFU, one through US SFU. If the relay works, you see
              each other&apos;s video.
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '0.75rem',
                alignItems: 'center',
              }}
            >
              <label htmlFor="poc-region">Region:</label>
              <select
                id="poc-region"
                value={pocRegion}
                onChange={(ev) => setPocRegion(ev.target.value as 'india-poc' | 'us-poc')}
                style={{ padding: '0.4rem 0.6rem' }}
              >
                <option value="india-poc">India (ultrakin.cloud/rtc-poc)</option>
                <option value="us-poc">US (ultrakin-us.cloud/rtc-poc)</option>
              </select>
              <button className="lk-button" onClick={joinPocMesh}>
                Join POC mesh
              </button>
            </div>
          </div>
        </div>
      </main>
      <footer data-lk-theme="default">
        Powered by{' '}
        <a href="https://ultrakin.com" rel="noopener">
          Ultrakin
        </a>{' '}
        | Professional Video Conferencing Platform
      </footer>
    </>
  );
}
